/**
 * Resolve a stored PropDocs body (template OR deal document) into the input the
 * DOCX-native editor wants. Shared by both containers — the storage contract is
 * identical (`{ body, body_storage_format }`), only the owning record differs.
 *
 * Formats, by `body_storage_format`:
 *   - 'docx' → base64 `.docx` bytes (what the editor's autosave writes) → decode
 *              to an ArrayBuffer and pass as `documentBuffer`.
 *   - 'json' → the body IS the editor's Document model → pass as `document`.
 *   - 'html' / unset (legacy CKEditor, base64 HTML) → convert ON OPEN via the
 *              browser-safe migration converter. In production a doc is normally
 *              migrated BEFORE its engine flag flips, so this is a fallback —
 *              but it guarantees ANY flagged doc opens, migrated or not.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

export interface ResolvedDocxBody<TDocument = unknown, TBuffer = unknown> {
  /** Editor Document model ('json' bodies). */
  document?: TDocument;
  /** Raw .docx bytes ('docx' bodies, or on-the-fly converted legacy HTML). */
  documentBuffer?: TBuffer;
  /** True while the async legacy-HTML conversion runs. */
  resolving: boolean;
}

/** Decode a base64 string to a fresh ArrayBuffer (isomorphic, browser atob). */
export function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/**
 * @param source     The stored body JSON — `{ body, body_storage_format }` at
 *                   the top level (a deal's `documents.document`, or a template
 *                   body). May also be the wrapper carrying `.template.body`.
 * @param reloadKey  Bump this to FORCE a re-resolve from the CURRENT source,
 *                   discarding the frozen mount body. Used by "Import new
 *                   version": the imported body must replace what's on screen
 *                   immediately. Left unchanged for autosave/publish refetches,
 *                   so those never clobber the user's live edit.
 */
export function useResolvedDocxBody<TDocument = unknown, TBuffer = unknown>(
  source: Record<string, any> | null | undefined,
  reloadKey?: number | string
): ResolvedDocxBody<TDocument, TBuffer> {
  // Resolve ONCE, from the first render carrying real content, then ignore later
  // changes. After mount the editor owns the live document; a parent refetch
  // (publish, autosave response, any Apollo cache update) returns the just-saved
  // body, and re-resolving would reload the editor and destroy the user's
  // in-progress edit + cursor. Freezing makes the editor the single source of
  // truth for the session — EXCEPT when `reloadKey` changes (an explicit
  // import), which re-freezes on the new source.
  const frozenRef = useRef<Record<string, any> | null>(null);
  const lastReloadKeyRef = useRef(reloadKey);
  if (reloadKey !== lastReloadKeyRef.current) {
    lastReloadKeyRef.current = reloadKey;
    frozenRef.current = null; // discard the old freeze → re-resolve below
  }
  const currentBody = source?.body ?? source?.template?.body;
  if (frozenRef.current === null && currentBody != null && String(currentBody).length > 0) {
    frozenRef.current = source ?? null;
  }
  const frozen = frozenRef.current ?? source;

  const format = frozen?.body_storage_format ?? frozen?.template?.body_storage_format;
  const body = frozen?.body ?? frozen?.template?.body;

  // Synchronous formats resolve immediately (no flash, no loading state).
  const sync = useMemo<ResolvedDocxBody<TDocument, TBuffer> | null>(() => {
    if (format === 'json' && body) {
      try {
        return {
          document: (typeof body === 'string' ? JSON.parse(body) : body) as TDocument,
          resolving: false,
        };
      } catch {
        return { resolving: false };
      }
    }
    if (format === 'docx' && typeof body === 'string' && body.length) {
      try {
        return { documentBuffer: base64ToArrayBuffer(body) as TBuffer, resolving: false };
      } catch {
        return { resolving: false };
      }
    }
    return null; // → legacy HTML (async) branch
  }, [format, body]);

  const [converted, setConverted] = useState<ResolvedDocxBody<TDocument, TBuffer> | null>(null);
  useEffect(() => {
    if (sync) {
      setConverted(null);
      return;
    }
    let cancelled = false;

    // A BLANK document (new deal doc: body === "") has nothing to convert, but
    // it still needs a real, minimal .docx to open — the editor renders its
    // "No document loaded" placeholder unless it receives a document or a
    // buffer. Returning `{resolving:false}` with neither left every new blank
    // doc permanently empty and uneditable.
    if (typeof body !== 'string' || !body.length) {
      setConverted({ resolving: true });
      (async () => {
        try {
          const { createEmptyDocx } = await import('@propdocs-editor/core/docx');
          const buffer = await createEmptyDocx();
          if (!cancelled) setConverted({ documentBuffer: buffer as TBuffer, resolving: false });
        } catch {
          if (!cancelled) setConverted({ resolving: false });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setConverted({ resolving: true });
    (async () => {
      try {
        // SAFETY: never run the HTML converter over a .docx.
        //
        // A base64 .docx reaching this branch (a stale/missing
        // `body_storage_format`) used to be decoded and — because ZIP binary
        // very often contains a byte sequence matching /<[a-z]/ — mistaken for
        // HTML. Converting that produced a VALID .docx whose single paragraph
        // held the raw binary as text: the document's real content was
        // destroyed. Detect the docx signature first and open it as one.
        const looksLikeDocx =
          body.startsWith('UEsD') || body.startsWith('PK'); // base64 'PK..' / raw ZIP
        if (looksLikeDocx) {
          try {
            const buf = base64ToArrayBuffer(body) as TBuffer;
            if (!cancelled) setConverted({ documentBuffer: buf, resolving: false });
            return;
          } catch {
            /* fall through — genuinely not decodable as a docx */
          }
        }

        // CKEditor bodies are base64-encoded HTML; tolerate raw HTML too.
        let html = body;
        try {
          const decoded = atob(body);
          // Require a real HTML tag, not an incidental byte match in binary.
          if (/<(?:!doctype|html|body|div|p|span|table|ol|ul|h[1-6])\b/i.test(decoded)) {
            html = decoded;
          }
        } catch {
          /* not base64 — treat `body` as raw HTML */
        }
        const { convertHtmlToDocx } = await import('../../../../utils/docx-migration-v2');
        const { buffer } = await convertHtmlToDocx(html, window.DOMParser);
        if (cancelled) return;
        setConverted({ documentBuffer: buffer as TBuffer, resolving: false });
      } catch {
        if (!cancelled) setConverted({ resolving: false }); // open empty on failure
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sync, body]);

  if (sync) return sync;
  return converted ?? { resolving: true };
}
